import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { DirectoryTree, Button, notification } from 'src/common';
import styles from './index.module.scss';
import { BlockTitle } from 'src/shared';
import { useTranslation } from 'react-i18next';
import { DataNode, EventDataNode } from 'rc-tree/lib/interface';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faFolderOpen } from '@fortawesome/pro-solid-svg-icons';
import { faChevronRight, faCloudDownload, faCommentQuestion, faCommentCheck } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import { getIconByFileType } from 'src/helpers/fileTypeIcon';
import { TenjinFile } from 'src/models/biddingGPT/types';
import { useDownloadTenjinFile, useTenjinFiles } from 'src/models/biddingGPT/hooks';
import UploadBiddingGPTFile from '../UploadBiddingGPTFile';

interface FileEventDataNode extends EventDataNode {
  filePath: string;
}

const childIcon = getIconByFileType('csv');

const getTreeData = (
  directoryTitle: string,
  directoryName: string,
  files: TenjinFile[],
  getTenjinFile: (targetPath: string) => void
): DataNode => {
  return {
    key: `${directoryName}-directory`,
    title: <span className={styles.folderTitle}>{directoryTitle}</span>,
    icon: ({ expanded }) => <FontAwesomeIcon icon={expanded ? faFolderOpen : faFolder} />,
    switcherIcon: ({ expanded }) => (
      <FontAwesomeIcon
        icon={faChevronRight}
        className={classNames(styles.expandIcon, { [styles.isExpanded]: expanded })}
      />
    ),
    children: files.map((file, index) => {
      return {
        key: `${directoryName}-${index}`,
        icon: <>{childIcon}</>,
        title: (
          <>
            <span className={styles.docTitle}>{file.name}</span>
            <Button
              type={'text'}
              className={classNames(styles.docAction, styles.fileAction)}
              onClick={e => {
                e.stopPropagation();
                file && getTenjinFile(`${directoryName}/${file.name}`);
              }}
              icon={<FontAwesomeIcon icon={faCloudDownload} />}
            />
          </>
        ),
        isLeaf: true,
        filePath: `${directoryName}/${file.name}`
      };
    })
  };
};

export const BiddingGPTFiles: FC = () => {
  const { t } = useTranslation();
  const { getTenjinFile } = useDownloadTenjinFile();
  const { data, previousData, loading, startPolling, stopPolling } = useTenjinFiles();

  const onClick = useCallback(
    (event: React.MouseEvent<Element, MouseEvent>, element: EventDataNode | FileEventDataNode) => {
      // leaf is always typeof FileEventDataNode
      if (element.isLeaf && 'filePath' in element && !loading) {
        getTenjinFile(element.filePath);
      }
    },
    [getTenjinFile, loading]
  );

  useEffect(() => {
    startPolling(5000);
    return () => {
      stopPolling();
    };
  });

  const newFiles = useMemo(() => {
    return data?.getFilesTenjin.answerFiles?.filter(
      file => !previousData?.getFilesTenjin.answerFiles?.some(({ name }) => name === file.name)
    );
  }, [data, previousData]);

  useEffect(() => {
    if (!!newFiles?.length && previousData) {
      notification.success({
        message: t('BiddingGPT.biddingGPTNotificationMessage'),
        description: t('BiddingGPT.biddingGPTNotificationDescription')
      });
    }
  }, [newFiles, previousData, t]);

  const treeDataAnswers: DataNode[] = useMemo(() => {
    if (data?.getFilesTenjin) {
      return [
        getTreeData(t('BiddingGPT.biddingGPTAnswers'), 'answers', data?.getFilesTenjin.answerFiles, getTenjinFile)
      ];
    } else return [];
  }, [data, getTenjinFile, t]);

  const treeDataQuestions: DataNode[] = useMemo(() => {
    if (data?.getFilesTenjin) {
      return [
        getTreeData(t('BiddingGPT.biddingGPTQuestions'), 'questions', data?.getFilesTenjin.questionFiles, getTenjinFile)
      ];
    } else return [];
  }, [data, getTenjinFile, t]);

  return (
    <div className={classNames(styles.biddingFeatureContainer)}>
      <BlockTitle
        icon={{ defaultIcon: faCommentQuestion }}
        title={t('BiddingGPT.biddingGPTQuestions')}
        className={styles.title}
      />
      <div className={styles.contentTree}>
        <DirectoryTree
          defaultExpandedKeys={Object.keys(treeDataQuestions)}
          className={classNames(styles.directoryTree, loading ? styles.loading : null)}
          selectable={false}
          onClick={onClick}
          treeData={treeDataQuestions}
        />
      </div>
      <UploadBiddingGPTFile />
      <BlockTitle
        icon={{ defaultIcon: faCommentCheck }}
        title={t('BiddingGPT.biddingGPTAnswers')}
        className={styles.title}
      />
      <div className={styles.contentTree}>
        <DirectoryTree
          defaultExpandedKeys={Object.keys(treeDataAnswers)}
          className={classNames(styles.directoryTree, loading ? styles.loading : null)}
          selectable={false}
          onClick={onClick}
          treeData={treeDataAnswers}
        />
      </div>
    </div>
  );
};

export default BiddingGPTFiles;
